import {
  getTenantOrganization,
  listTenantOrganizations,
} from "../../services/organizationService";

/** Shared in-memory orgId → display name cache */
const cache: Record<string, string> = {};
const inflight = new Map<string, Promise<string>>();
let listLoaded = false;
let listPromise: Promise<void> | null = null;

function put(id: any, name: any) {
  const key = String(id || "").trim();
  const label = String(name || "").trim();
  if (!key || !label) return;
  cache[key] = label;
  if (key.length === 24) cache[key.toLowerCase()] = label;
}

function extractOrgRows(res: any): any[] {
  if (!res || res.success === 0) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res.data)) return res.data;
  if (Array.isArray(res.data?.items)) return res.data.items;
  if (Array.isArray(res.data?.data)) return res.data.data;
  if (Array.isArray(res.items)) return res.items;
  if (Array.isArray(res.organizations)) return res.organizations;
  return [];
}

export function getCachedOrgName(organizationId: any): string {
  if (organizationId == null || organizationId === "") return "";
  if (typeof organizationId === "object") {
    if (organizationId.name || organizationId.brandName || organizationId.legalName) {
      return String(
        organizationId.name ||
          organizationId.brandName ||
          organizationId.legalName
      );
    }
    organizationId =
      organizationId.$oid || organizationId._id || organizationId.id;
  }
  const key = String(organizationId || "").trim();
  if (!key || key === "all" || key === "[object Object]") return "";
  return cache[key] || cache[key.toLowerCase()] || "";
}

export function seedOrgNameMap(
  entries: Array<{ id?: any; _id?: any; name?: any; brandName?: any; legalName?: any; slug?: any } | any>
) {
  (entries || []).forEach((o: any) => {
    if (!o) return;
    put(
      o._id || o.id || o.organizationId,
      o.name || o.brandName || o.legalName || o.slug || o.organizationName
    );
  });
}

let rawList: any = null;

/** One shared /tenant-organizations request for the header and every page. */
export function loadTenantOrganizationsOnce(): Promise<any> {
  if (rawList) return Promise.resolve(rawList);
  if (!listPromise) {
    listPromise = (async () => {
      try {
        const res = await listTenantOrganizations();
        rawList = res;
        extractOrgRows(res).forEach((o) => {
          put(
            o?._id || o?.id,
            o?.name || o?.brandName || o?.legalName || o?.slug
          );
        });
      } catch (_) {
        rawList = { success: 0, data: [] };
      } finally {
        listLoaded = true;
        listPromise = null;
      }
    })();
  }
  return listPromise.then(() => rawList);
}

export async function ensureOrgListLoaded(): Promise<Record<string, string>> {
  await loadTenantOrganizationsOnce();
  return { ...cache };
}

export async function resolveOrgNameAsync(organizationId: any): Promise<string> {
  const existing = getCachedOrgName(organizationId);
  if (existing) return existing;

  await ensureOrgListLoaded();
  const afterList = getCachedOrgName(organizationId);
  if (afterList) return afterList;

  let key = organizationId;
  if (key && typeof key === "object") {
    key = key.$oid || key._id || key.id;
  }
  key = String(key || "").trim();
  if (!key || key.length < 12 || key === "all") return "";

  if (inflight.has(key)) return inflight.get(key)!;

  const p = (async () => {
    try {
      const res = await getTenantOrganization(key);
      const org = res?.data || res;
      const name =
        org?.name || org?.brandName || org?.legalName || org?.slug || "";
      if (name) put(key, name);
      return String(name || "");
    } catch (_) {
      return "";
    } finally {
      inflight.delete(key);
    }
  })();

  inflight.set(key, p);
  return p;
}

export function snapshotOrgNameMap(): Record<string, string> {
  return { ...cache };
}
